import React from 'react';
import { Droplet, Zap, MapPin, Info as InfoIcon, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TRAVEL_FEE_INFO, businessInfo } from '../utils/mock';

const Info = () => {
  return (
    <section id="info" className="py-24 bg-zinc-950">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-blue-600/20 border border-blue-500/50 rounded-full px-4 py-2 mb-4">
            <span className="text-blue-500 font-semibold text-sm">Important Information</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Before Your Appointment
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            What you need to know to prepare for our mobile detailing service
          </p>
        </div>

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          {/* Water Hookup Card */}
          <Card className="bg-zinc-900 border-zinc-800 hover:border-blue-500/50 transition-all">
            <CardHeader>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-blue-600/20 border border-blue-500/50 rounded-lg flex items-center justify-center">
                  <Droplet className="w-6 h-6 text-blue-500" />
                </div>
                <CardTitle className="text-white text-2xl">Water Hookup Required</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-300">
                Access to a standard outdoor water spigot/hose connection is required for our mobile detailing service.
              </p>
              <div className="space-y-2">
                <p className="text-gray-400 font-medium">What we need:</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Outdoor garden hose connection (standard ¾" fitting)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Clear access to water source (within 50 feet of vehicle)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>We bring all hoses and equipment</span>
                  </li>
                </ul>
              </div>
              <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-300">
                  <span className="font-semibold">Note:</span> If you don't have water access, please contact us to discuss alternative arrangements.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Electrical Hookup Card */}
          <Card className="bg-zinc-900 border-zinc-800 hover:border-yellow-500/50 transition-all">
            <CardHeader>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-yellow-600/20 border border-yellow-500/50 rounded-lg flex items-center justify-center">
                  <Zap className="w-6 h-6 text-yellow-500" />
                </div>
                <CardTitle className="text-white text-2xl">Power Outlet Required</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-300">
                A standard 120V household electrical outlet is required to power our professional detailing equipment.
              </p>
              <div className="space-y-2">
                <p className="text-gray-400 font-medium">What we need:</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Standard 120V outlet (15-20 amp circuit)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Outdoor outlet preferred (GFCI protected)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Within 100 feet of vehicle (we bring extension cords)</span>
                  </li>
                </ul>
              </div>
              <div className="p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-300">
                  <span className="font-semibold">Note:</span> Indoor outlets work too! We'll run cords through a door or window.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Travel Fee Card - Full Width */}
          <Card className="bg-zinc-900 border-zinc-800 hover:border-green-500/50 transition-all md:col-span-2">
            <CardHeader>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-green-600/20 border border-green-500/50 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-green-500" />
                </div>
                <CardTitle className="text-white text-2xl">Service Area & Travel Fees</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-gray-300 mb-4">
                  We're based in <span className="font-semibold text-white">{businessInfo.address}</span> and provide mobile detailing throughout the Lower Mainland.
                </p>
                <p className="text-lg font-semibold text-green-400 mb-3">
                  ✓ FREE Travel within {TRAVEL_FEE_INFO.freeRadius} km radius
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Free Service Zones */}
                <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                  <h3 className="text-green-400 font-bold mb-3 flex items-center space-x-2">
                    <CheckCircle2 className="w-5 h-5" />
                    <span>Free Service Areas</span>
                  </h3>
                  <p className="text-sm text-gray-400 mb-2">No travel fee for these locations:</p>
                  <ul className="space-y-1.5 text-gray-300">
                    {TRAVEL_FEE_INFO.freeZones.map(zone => (
                      <li key={zone} className="flex items-center space-x-2">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                        <span>{zone}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Extended Service Zones */}
                <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                  <h3 className="text-blue-400 font-bold mb-3 flex items-center space-x-2">
                    <InfoIcon className="w-5 h-5" />
                    <span>Extended Coverage</span>
                  </h3>
                  <p className="text-sm text-gray-400 mb-2">Travel surcharge applies:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-300">
                    {TRAVEL_FEE_INFO.extendedZones.map(zone => (
                      <div key={zone.name} className="flex justify-between">
                        <span>{zone.name}</span>
                        <span className="font-semibold text-blue-400">+${zone.fee}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-4 bg-zinc-800 border border-zinc-700 rounded-lg">
                <p className="text-gray-300 text-sm">
                  <span className="font-semibold text-white">💡 Note:</span> Travel fees (if applicable) will be confirmed when you book your appointment. We'll always let you know the total cost upfront - no surprises!
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Disclaimer */}
          <Card className="bg-zinc-900 border-zinc-800 md:col-span-2">
            <CardContent className="p-6">
              <div className="flex items-start space-x-3">
                <InfoIcon className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-blue-400 font-semibold mb-2">Pricing Information</h3>
                  <p className="text-gray-300 text-sm">
                    Prices listed are starting rates for standard conditions. <span className="font-semibold text-white">Final pricing may vary based on vehicle condition, size, and level of detailing required.</span> We'll always provide a final quote before beginning any work - no surprises!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact CTA */}
          <Card className="bg-gradient-to-br from-red-600/20 to-red-700/10 border-red-500/30 md:col-span-2">
            <CardContent className="p-8 text-center">
              <h3 className="text-white text-2xl font-bold mb-2">Questions About Setup?</h3>
              <p className="text-gray-300 mb-6">
                Not sure if your location has the required hookups? Give us a call and we'll help you figure it out!
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href={`tel:${businessInfo.phone}`}
                  className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded-lg transition-all hover:scale-105"
                >
                  <span>Call Us: {businessInfo.phone}</span>
                </a>
                <button
                  onClick={() => {
                    const element = document.getElementById('booking');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center space-x-2 bg-white hover:bg-gray-100 text-black font-semibold px-8 py-3 rounded-lg transition-all hover:scale-105"
                >
                  <span>Book Now</span>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Info;
